import { zodResolver } from "@hookform/resolvers/zod";
import React from "react";
import { Controller, useForm, useFormContext, useWatch } from "react-hook-form";
import { z } from "zod";

import {
  CHARGE_CANCELLED_IN_PROGRESS_TESTS_SETTING,
  parseChargeCancelledInProgressTests,
} from "@blis/shared/schemas/settings";
import { trpc } from "@blis/trpc/react";

import { Button } from "@/components/ui/button";
import {
  Description,
  FieldError,
  Input,
  Label,
  TextField,
} from "@/components/ui/field";
import { NumberField, NumberInput } from "@/components/ui/number-field";
import { Radio, RadioField, RadioGroup } from "@/components/ui/radio-group";
import { Switch, SwitchField } from "@/components/ui/switch";
import { toast } from "@/components/ui/toast";
import { mutationResponse } from "@/utils/trpc";
import { useSettings } from "../../hooks";
import { SettingsGroup, SettingsGroupForm } from "../settings/SettingsGroup";

export function BillingSettings() {
  return (
    <div className="flex flex-col gap-6 max-w-3xl">
      <CancelledTestChargeSettings />
      <InvoiceSettings />
      <StatFeeSettings />
      <MasterInvoiceSettings />
    </div>
  );
}

function CancelledTestChargeSettings() {
  const { settings } = useSettings();
  const utils = trpc.useUtils();
  const update = trpc.settings.update.useMutation();
  const charge = parseChargeCancelledInProgressTests(
    settings[CHARGE_CANCELLED_IN_PROGRESS_TESTS_SETTING],
  );

  return (
    <SettingsGroup
      title="Cancelled tests"
      description="Controls whether cancel of a test that already joined a run still bills the client."
    >
      <SwitchField>
        <Switch
          isSelected={charge}
          onChange={(val) => {
            update.mutate(
              [
                {
                  name: CHARGE_CANCELLED_IN_PROGRESS_TESTS_SETTING,
                  value: val.toString(),
                },
              ],
              mutationResponse({
                errorTitle: "Error updating setting",
                successMessage: val
                  ? "Cancelled in-progress tests will be charged"
                  : "Cancelled in-progress tests will not be charged",
                onSuccess() {
                  void utils.settings.list.invalidate();
                },
              }),
            );
          }}
        >
          Charge for cancelled in-progress tests
        </Switch>
        <Description>
          Off (default): cancel does not bill, including tests already on a run.
          Same as deleting a mistaken extra test. On: cancel of a test on a run
          still bills.
        </Description>
      </SwitchField>
    </SettingsGroup>
  );
}

const invoiceSchema = z.object({
  invoiceFormat: z.enum(["pdf", "excel"]),
});
type InvoiceFormValues = z.infer<typeof invoiceSchema>;

function InvoiceSettings() {
  return (
    <SettingsGroupForm
      title="Invoice format"
      description="The format of the invoice that will be generated."
      schema={invoiceSchema}
    >
      <InvoiceFormFields />
    </SettingsGroupForm>
  );
}

function InvoiceFormFields() {
  const { control } = useFormContext<InvoiceFormValues>();
  return (
    <div className="flex flex-col gap-2">
      <Controller
        control={control}
        name="invoiceFormat"
        render={({ field }) => (
          <RadioGroup
            aria-label="Select file type"
            value={field.value || "pdf"}
            onChange={(value) => {
              field.onChange(value);
            }}
          >
            <RadioField>
              <Radio value="pdf">PDF</Radio>
            </RadioField>
            <RadioField>
              <Radio value="excel">Excel</Radio>
            </RadioField>
          </RadioGroup>
        )}
      />
    </div>
  );
}

const statFeeSchema = z.object({
  "fees-stat": z.string(),
});
type StatFeeFormValues = z.infer<typeof statFeeSchema>;

function StatFeeSettings() {
  return (
    <SettingsGroupForm
      title="STAT fee"
      description="The fee that will be added to invoices for any accession that is marked as STAT."
      schema={statFeeSchema}
    >
      <StatFeeFormFields />
    </SettingsGroupForm>
  );
}

function StatFeeFormFields() {
  const { control } = useFormContext<StatFeeFormValues>();
  return (
    <div className="flex flex-col gap-2">
      <Controller
        control={control}
        name="fees-stat"
        render={({ field, fieldState: { error } }) => (
          <NumberField
            className="w-48"
            aria-label="STAT fee"
            name={field.name}
            value={field.value ? Number(field.value) : 0}
            onChange={(val) => {
              const newValue = Number.isNaN(val) ? 0 : val;
              field.onChange(newValue.toString());
            }}
            onBlur={field.onBlur}
            minValue={0}
            isInvalid={!!error}
            validationBehavior="aria"
          >
            <NumberInput />
            <FieldError>{error?.message}</FieldError>
          </NumberField>
        )}
      />
    </div>
  );
}

type MasterInvoiceFormValues = {
  masterInvoiceEnabled: "true" | "false";
  masterInvoiceEmail: string;
  invoiceNotificationMode: "per_document" | "master_only";
};

const masterInvoiceSchema = z
  .object({
    masterInvoiceEnabled: z.enum(["true", "false"]),
    masterInvoiceEmail: z.string(),
    invoiceNotificationMode: z.enum(["per_document", "master_only"]),
  })
  .refine(
    (data) => data.masterInvoiceEnabled !== "true" || !!data.masterInvoiceEmail,
    {
      message: "Email is required when master invoice is enabled",
      path: ["masterInvoiceEmail"],
    },
  )
  .refine(
    (data) =>
      data.masterInvoiceEnabled !== "true" ||
      !data.masterInvoiceEmail ||
      z.email().safeParse(data.masterInvoiceEmail).success,
    {
      message: "Must be a valid email address",
      path: ["masterInvoiceEmail"],
    },
  );

function MasterInvoiceSettings() {
  const { settings } = useSettings();
  const mutation = trpc.settings.update.useMutation();

  const form = useForm<
    MasterInvoiceFormValues,
    unknown,
    MasterInvoiceFormValues
  >({
    defaultValues: {
      masterInvoiceEnabled:
        (settings.masterInvoiceEnabled as "true" | "false") ?? "false",
      masterInvoiceEmail: settings.masterInvoiceEmail ?? "",
      invoiceNotificationMode:
        (settings.invoiceNotificationMode as "per_document" | "master_only") ??
        "per_document",
    },
    resolver: zodResolver(masterInvoiceSchema),
  });

  React.useEffect(() => {
    form.reset({
      masterInvoiceEnabled:
        (settings.masterInvoiceEnabled as "true" | "false") ?? "false",
      masterInvoiceEmail: settings.masterInvoiceEmail ?? "",
      invoiceNotificationMode:
        (settings.invoiceNotificationMode as "per_document" | "master_only") ??
        "per_document",
    });
  }, [
    form,
    settings.masterInvoiceEnabled,
    settings.masterInvoiceEmail,
    settings.invoiceNotificationMode,
  ]);

  const onSubmit = async (values: MasterInvoiceFormValues) => {
    await mutation.mutateAsync(
      Object.entries(values).map(([name, value]) => ({ name, value })),
      {
        onSuccess() {
          toast.success("Settings updated");
        },
        onError(err) {
          toast.error("Error updating settings", { description: err.message });
        },
      },
    );
  };

  return (
    <SettingsGroup
      title="Master invoice"
      description="Generate a combined invoice of all line items and send it to an internal recipient."
    >
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <MasterInvoiceFormFields form={form} />
        <div className="flex justify-end mt-3">
          <Button type="submit" isPending={mutation.isPending}>
            Save
          </Button>
        </div>
      </form>
    </SettingsGroup>
  );
}

function MasterInvoiceFormFields({
  form,
}: {
  form: ReturnType<
    typeof useForm<MasterInvoiceFormValues, unknown, MasterInvoiceFormValues>
  >;
}) {
  const enabled =
    useWatch({ control: form.control, name: "masterInvoiceEnabled" }) ===
    "true";

  return (
    <div className="flex flex-col gap-4">
      <Controller
        control={form.control}
        name="masterInvoiceEnabled"
        render={({ field }) => (
          <SwitchField>
            <Switch
              isSelected={field.value === "true"}
              onChange={(checked) => field.onChange(checked ? "true" : "false")}
            >
              Enable master invoice
            </Switch>
          </SwitchField>
        )}
      />

      {enabled && (
        <>
          <Controller
            control={form.control}
            name="masterInvoiceEmail"
            render={({ field, fieldState: { error } }) => (
              <TextField
                aria-label="Master invoice email"
                isInvalid={!!error}
                validationBehavior="aria"
              >
                <Label>Recipient email</Label>
                <Input
                  type="email"
                  placeholder="billing@example.com"
                  {...field}
                />
                <FieldError>{error?.message}</FieldError>
              </TextField>
            )}
          />

          <Controller
            control={form.control}
            name="invoiceNotificationMode"
            render={({ field }) => (
              <SwitchField>
                <Switch
                  isSelected={field.value === "master_only"}
                  onChange={(checked) =>
                    field.onChange(checked ? "master_only" : "per_document")
                  }
                >
                  Send single summary email
                </Switch>
                <p className="text-sm text-gray-500 mt-1">
                  Consolidate all invoice notifications into a single email with
                  the master invoice attached and a summary of all client
                  invoices. Individual client emails will not be sent.
                </p>
              </SwitchField>
            )}
          />
        </>
      )}
    </div>
  );
}
